import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useApp } from "@/context/AppContext";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { Mail, User as UserIcon, CheckCircle2, ArrowLeft, Sparkles, MapPin } from "lucide-react";
import PasswordInput from "@/components/PasswordInput";
import {
  otpStatus, signupStart, signupResend, signupVerify,
  passwordForgot, passwordResend, passwordVerify, passwordReset,
} from "@/lib/api";

const Label = ({ children }) => <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">{children}</label>;
const Field = ({ icon: Icon, prefix, children }) => (
  <div className="mt-1 flex items-center border border-slate-200 rounded-xl px-3 focus-within:border-emerald-500">
    {Icon && <Icon className="w-4 h-4 text-slate-400 flex-shrink-0" />}
    {prefix && <span className="text-sm text-slate-500 font-medium">{prefix}</span>}
    {children}
  </div>
);
const inputCls = "w-full min-w-0 px-3 py-3 text-sm outline-none";
const primaryBtn = "w-full bg-emerald-700 hover:bg-emerald-800 disabled:opacity-60 text-white rounded-xl py-3 text-sm font-semibold transition-colors";

function useCooldown() {
  const [left, setLeft] = useState(0);
  useEffect(() => {
    if (left <= 0) return undefined;
    const id = setTimeout(() => setLeft((s) => s - 1), 1000);
    return () => clearTimeout(id);
  }, [left]);
  return [left, setLeft];
}

function OtpRow({ label, verified, onVerify, onResend, cooldown, testid, busy }) {
  const { t } = useTranslation();
  const [otp, setOtp] = useState("");
  return (
    <div>
      <Label>{label}</Label>
      {verified ? (
        <div className="mt-1 flex items-center gap-2 text-sm font-semibold text-emerald-700 border border-emerald-100 bg-emerald-50 rounded-xl px-3 py-3" data-testid={`${testid}-verified`}>
          <CheckCircle2 className="w-4 h-4" /> {t("auth.verified")}
        </div>
      ) : (
        <>
          <div className="mt-1 flex items-center gap-2">
            <input
              value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))} inputMode="numeric" autoComplete="one-time-code"
              placeholder={t("auth.enterOtp")} className="flex-1 min-w-0 border border-slate-200 rounded-xl px-3 py-3 text-sm tracking-[0.3em] outline-none focus:border-emerald-500" data-testid={`${testid}-input`}
            />
            <button type="button" disabled={otp.length !== 6 || busy} onClick={() => onVerify(otp)} className="flex-shrink-0 bg-emerald-700 hover:bg-emerald-800 disabled:opacity-50 text-white rounded-xl px-4 py-3 text-sm font-semibold" data-testid={`${testid}-verify`}>
              {t("auth.verify")}
            </button>
          </div>
          <button type="button" disabled={cooldown > 0} onClick={onResend} className="mt-1.5 text-xs font-semibold text-emerald-700 disabled:text-slate-400" data-testid={`${testid}-resend`}>
            {cooldown > 0 ? t("auth.resendIn", { s: cooldown }) : t("auth.resend")}
          </button>
        </>
      )}
    </div>
  );
}

export default function AuthDialog({ open, onOpenChange }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { login, completeSignup, formatApiError } = useApp();
  const [view, setView] = useState("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [signupPin, setSignupPin] = useState("");
  const [identifier, setIdentifier] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [providers, setProviders] = useState(null);
  const [signup, setSignup] = useState(null);
  const [verified, setVerified] = useState({ email: false, sms: false });
  const [emailCd, setEmailCd] = useCooldown();
  const [smsCd, setSmsCd] = useCooldown();
  const [resetId, setResetId] = useState(null);
  const [resetToken, setResetToken] = useState(null);
  const [resetCd, setResetCd] = useCooldown();

  useEffect(() => { if (open && view === "signup" && !providers) otpStatus().then(setProviders).catch(() => {}); }, [open, view, providers]);

  const resetAll = () => {
    setView("login"); setEmail(""); setPassword(""); setConfirm(""); setName(""); setPhone(""); setSignupPin(""); setIdentifier("");
    setError(""); setSignup(null); setVerified({ email: false, sms: false }); setResetId(null); setResetToken(null);
  };
  const go = (v) => { setError(""); setView(v); };
  const run = async (fn) => {
    setError(""); setLoading(true);
    try { await fn(); } catch (err) { setError(formatApiError(err)); } finally { setLoading(false); }
  };

  const submitLogin = (e) => { e.preventDefault(); run(async () => {
    const u = await login(email, password);
    toast.success(`${t("auth.welcomeBack")} ${u.name}`);
    onOpenChange(false); resetAll();
  }); };

  const submitSignup = (e) => {
    e.preventDefault();
    if (!/^[1-9]\d{5}$/.test(signupPin)) { setError("Sahi 6-digit PIN code daalein (sirf numbers, 0 se shuru nahi)."); return; }
    run(async () => {
    const res = await signupStart({ email, password, name, phone, pincode: signupPin });
    setSignup(res); setEmailCd(res.cooldown); setSmsCd(res.cooldown); go("otp");
  }); };

  const verifyChannel = (channel) => (otp) => run(async () => {
    const res = await signupVerify(signup.signup_id, channel, otp);
    if (res.completed) { await completeSignup(res.user); go("welcome"); return; }
    setVerified({ email: res.email_verified, sms: res.phone_verified });
  });
  const resendChannel = (channel) => () => run(async () => {
    const res = await signupResend(signup.signup_id, channel);
    (channel === "email" ? setEmailCd : setSmsCd)(res.cooldown || 60);
  });

  const submitForgot = (e) => { e.preventDefault(); run(async () => {
    const res = await passwordForgot(identifier);
    setResetId(res.reset_id); setResetCd(res.cooldown); toast.info(res.message); go("forgotOtp");
  }); };
  const verifyReset = (otp) => run(async () => {
    const res = await passwordVerify(resetId, otp);
    setResetToken(res.reset_token); setPassword(""); setConfirm(""); go("newPassword");
  });
  const submitNewPassword = (e) => {
    e.preventDefault();
    if (password !== confirm) { setError(t("auth.mismatch")); return; }
    run(async () => { await passwordReset(resetToken, password); setPassword(""); setConfirm(""); go("resetDone"); });
  };

  const titles = {
    login: [t("auth.welcomeBack"), t("auth.signinSub")],
    signup: [t("auth.createAccount"), t("auth.signupSub")],
    otp: [t("auth.verifyTitle"), signup ? (signup.sms_required ? `${signup.email} · +91 ••••••${signup.phone_last4}` : signup.email) : ""],
    forgot: [t("auth.forgotTitle"), t("auth.forgotSub")],
    forgotOtp: [t("auth.forgotTitle"), t("auth.enterOtp")],
    newPassword: [t("auth.forgotTitle"), t("auth.newPassword")],
    resetDone: [t("auth.forgotTitle"), ""],
    welcome: [t("auth.welcomeTitle"), t("auth.welcomeSub")],
  };
  const notConfigured = view === "signup" && providers && !providers.email;

  return (
    <Dialog open={open} onOpenChange={(v) => { onOpenChange(v); if (!v) resetAll(); }}>
      <DialogContent className="max-w-md p-0 overflow-hidden rounded-2xl max-h-[92vh] overflow-y-auto" data-testid="auth-dialog">
        <div className="relative bg-gradient-to-br from-emerald-700 to-emerald-900 text-white px-6 pt-6 pb-8">
          <div className="flex items-center gap-2 mb-4">
            <div className="inline-block leading-tight bg-white rounded-xl px-3 py-2 shadow-sm" data-testid="auth-wordmark">
                <div className="font-display text-xl font-bold text-black leading-none tracking-tight">Ver<span className="text-emerald-700">B</span></div>
                <div className="text-[10px] font-semibold tracking-[0.16em] text-black mt-0.5 whitespace-nowrap">Vertual <span className="text-emerald-700">Bazaar</span></div>
              </div>
          </div>
          <DialogHeader>
            <DialogTitle className="text-2xl font-display font-bold text-white" data-testid="auth-title">{titles[view][0]}</DialogTitle>
          </DialogHeader>
          {titles[view][1] && <p className="text-sm text-emerald-100 mt-1 break-words">{titles[view][1]}</p>}
        </div>

        <div className="p-6 space-y-4">
          {["forgot", "forgotOtp", "newPassword"].includes(view) && (
            <button type="button" onClick={() => go("login")} className="inline-flex items-center gap-1 text-xs font-semibold text-slate-500 hover:text-emerald-700" data-testid="auth-back-login">
              <ArrowLeft className="w-3.5 h-3.5 rtl-flip" /> {t("auth.loginBtn")}
            </button>
          )}

          {view === "login" && (
            <form onSubmit={submitLogin} className="space-y-3">
              <div>
                <Label>{t("auth.email")}</Label>
                <Field icon={Mail}><input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required placeholder="you@example.com" className={inputCls} data-testid="auth-email-input" /></Field>
              </div>
              <div>
                <div className="flex items-center justify-between">
                  <Label>{t("auth.password")}</Label>
                  <button type="button" onClick={() => { setIdentifier(email); go("forgot"); }} className="text-xs font-semibold text-emerald-700 hover:underline" data-testid="forgot-password-link">{t("auth.forgot")}</button>
                </div>
                <PasswordInput value={password} onChange={setPassword} placeholder="Min 6 characters" testid="auth-password-input" />
              </div>
              {error && <div className="text-xs bg-red-50 text-red-700 border border-red-100 rounded-lg px-3 py-2" data-testid="auth-error">{error}</div>}
              <button type="submit" disabled={loading} className={primaryBtn} data-testid="auth-submit-btn">{loading ? t("auth.wait") : t("auth.loginBtn")}</button>
            </form>
          )}

          {view === "signup" && (
            <form onSubmit={submitSignup} className="space-y-3">
              {notConfigured && <div className="text-xs bg-amber-50 text-amber-800 border border-amber-100 rounded-lg px-3 py-2" data-testid="otp-not-configured">Verification service abhi set nahi hai. Kripya thodi der baad try karein.</div>}
              <div>
                <Label>{t("auth.fullName")}</Label>
                <Field icon={UserIcon}><input value={name} onChange={(e) => setName(e.target.value)} required minLength={2} maxLength={80} placeholder="Your full name" className={inputCls} data-testid="signup-name-input" /></Field>
              </div>
              <div>
                <Label>{t("auth.mobile")}</Label>
                <Field prefix="+91"><input value={phone} onChange={(e) => setPhone(e.target.value.replace(/\D/g, "").slice(0, 10))} required pattern="[6-9][0-9]{9}" inputMode="numeric" placeholder="98XXX XXXXX" className={inputCls} data-testid="signup-phone-input" /></Field>
              </div>
              <div>
                <Label>PIN code *</Label>
                <Field icon={MapPin}><input value={signupPin} onChange={(e) => { setSignupPin(e.target.value.replace(/\D/g, "").slice(0, 6)); setError(""); }} required inputMode="numeric" maxLength={6} placeholder="6-digit PIN, e.g. 221005" className={inputCls} data-testid="signup-pincode-input" /></Field>
                {signupPin.length > 0 && !/^[1-9]\d{5}$/.test(signupPin) && <div className="text-[11px] text-rose-600 mt-1" data-testid="signup-pincode-error">PIN code 6 digits ka hona chahiye aur 0 se shuru nahi hota.</div>}
              </div>
              <div>
                <Label>{t("auth.email")}</Label>
                <Field icon={Mail}><input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required placeholder="you@example.com" className={inputCls} data-testid="auth-email-input" /></Field>
              </div>
              <div>
                <Label>{t("auth.password")}</Label>
                <PasswordInput value={password} onChange={setPassword} placeholder="Min 6 characters" testid="auth-password-input" autoComplete="new-password" />
              </div>
              {error && <div className="text-xs bg-red-50 text-red-700 border border-red-100 rounded-lg px-3 py-2" data-testid="auth-error">{error}</div>}
              <button type="submit" disabled={loading || notConfigured} className={primaryBtn} data-testid="auth-submit-btn">{loading ? t("auth.wait") : t("auth.createBtn")}</button>
            </form>
          )}

          {view === "otp" && signup && (
            <div className="space-y-4" data-testid="signup-otp-step">
              <OtpRow label={t("auth.emailOtp")} verified={verified.email} onVerify={verifyChannel("email")} onResend={resendChannel("email")} cooldown={emailCd} testid="signup-email-otp" busy={loading} />
              {signup.sms_required && <OtpRow label={t("auth.mobileOtp")} verified={verified.sms} onVerify={verifyChannel("sms")} onResend={resendChannel("sms")} cooldown={smsCd} testid="signup-sms-otp" busy={loading} />}
              {error && <div className="text-xs bg-red-50 text-red-700 border border-red-100 rounded-lg px-3 py-2" data-testid="auth-error">{error}</div>}
            </div>
          )}

          {view === "forgot" && (
            <form onSubmit={submitForgot} className="space-y-3" data-testid="forgot-step">
              <div>
                <Label>{t("auth.identifier")}</Label>
                <Field icon={Mail}><input value={identifier} onChange={(e) => setIdentifier(e.target.value)} required placeholder="you@example.com / 98XXXXXXXX" className={inputCls} data-testid="forgot-identifier-input" /></Field>
              </div>
              {error && <div className="text-xs bg-red-50 text-red-700 border border-red-100 rounded-lg px-3 py-2" data-testid="auth-error">{error}</div>}
              <button type="submit" disabled={loading} className={primaryBtn} data-testid="forgot-send-otp-btn">{loading ? t("auth.wait") : t("auth.sendOtp")}</button>
            </form>
          )}

          {view === "forgotOtp" && (
            <div className="space-y-3" data-testid="forgot-otp-step">
              <OtpRow label="OTP" verified={false} onVerify={verifyReset} onResend={() => run(async () => { const r = await passwordResend(resetId); setResetCd(r.cooldown || 60); })} cooldown={resetCd} testid="forgot-otp" busy={loading} />
              {error && <div className="text-xs bg-red-50 text-red-700 border border-red-100 rounded-lg px-3 py-2" data-testid="auth-error">{error}</div>}
            </div>
          )}

          {view === "newPassword" && (
            <form onSubmit={submitNewPassword} className="space-y-3" data-testid="new-password-step">
              <div>
                <Label>{t("auth.newPassword")}</Label>
                <PasswordInput value={password} onChange={setPassword} placeholder="Min 6 characters" testid="new-password-input" autoComplete="new-password" />
              </div>
              <div>
                <Label>{t("auth.confirmPassword")}</Label>
                <PasswordInput value={confirm} onChange={setConfirm} placeholder="Min 6 characters" testid="confirm-password-input" autoComplete="new-password" />
              </div>
              {error && <div className="text-xs bg-red-50 text-red-700 border border-red-100 rounded-lg px-3 py-2" data-testid="auth-error">{error}</div>}
              <button type="submit" disabled={loading} className={primaryBtn} data-testid="update-password-btn">{loading ? t("auth.wait") : t("auth.updatePassword")}</button>
            </form>
          )}

          {view === "resetDone" && (
            <div className="space-y-4 text-center" data-testid="reset-done">
              <CheckCircle2 className="w-10 h-10 text-emerald-600 mx-auto" />
              <p className="text-sm font-semibold text-slate-800">{t("auth.pwUpdated")}</p>
              <button type="button" onClick={() => { setPassword(""); go("login"); }} className={primaryBtn} data-testid="continue-login-btn">{t("auth.continueLogin")}</button>
            </div>
          )}

          {view === "welcome" && (
            <div className="space-y-4 text-center" data-testid="welcome-note">
              <Sparkles className="w-10 h-10 text-amber-500 mx-auto" />
              <p className="text-sm text-slate-600">{t("auth.welcomeSub")}</p>
              <button type="button" onClick={() => { onOpenChange(false); resetAll(); navigate("/onboarding"); }} className={primaryBtn} data-testid="welcome-setup-activities-btn">{t("ob.setup")}</button>
              <button type="button" onClick={() => { onOpenChange(false); resetAll(); }} className="w-full rounded-full border border-slate-200 hover:border-emerald-500 px-5 py-2.5 text-sm font-semibold text-slate-700" data-testid="welcome-explore-btn">{t("auth.explore")}</button>
            </div>
          )}

          {(view === "login" || view === "signup") && (
            <div className="text-center text-xs text-slate-500">
              {view === "login" ? (
                <>{t("auth.newHere")} <button onClick={() => go("signup")} className="text-emerald-700 font-semibold underline" data-testid="switch-signup">{t("auth.signup")}</button></>
              ) : (
                <>{t("auth.haveAccount")} <button onClick={() => go("login")} className="text-emerald-700 font-semibold underline" data-testid="switch-login">{t("auth.loginBtn")}</button></>
              )}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
